import { createFlagOverrideStorage } from '@quickmd-llc/design-system'
import { defaultFlags } from 'store/flags/defaultFlags'
import { isDeployedToProduction } from 'utils/isDeployedToProduction'

// Namespaced per app rather than shared, so two QuickMD apps served from the same origin in a
// preview environment cannot pin each other's flags.
const STORAGE_KEY = 'devToolsFlagOverrides'

/** Every flag the app reads, in the order the panel lists them. */
export const FLAG_KEYS: string[] = Object.keys(defaultFlags).sort((left, right) => left.localeCompare(right))

/**
 * Overrides are gated on the *deployment*, not on `isDevToolsEnabled`.
 *
 * Dev Tools are deliberately reachable in production — `getIsDevToolsEnabled` opens them for
 * anyone with `?devTools=true` or the localStorage key, which is how production issues get
 * inspected. A panel that could also rewrite flags there would be a live change to what
 * clinicians see, from a URL parameter. So the override layer keys off the hostname instead and
 * is inert everywhere production is served, however Dev Tools were opened.
 */
export const areFlagOverridesAllowed = () => !isDeployedToProduction

/**
 * Whether `key` names a real flag.
 *
 * Stale keys are dropped rather than passed through: a flag that has since been renamed or
 * removed would otherwise be resurrected into the store with a shape the app no longer expects.
 */
export const isFlagKey = (key: string) => Object.hasOwn(defaultFlags, key)

// The storage half is shared with the other clients, which all needed the same pinning
// behaviour. It deals in `Record<string, unknown>` because it has no idea what shape any given
// flag has — which happens to be exactly how this app types its flags too.
const storage = createFlagOverrideStorage({
  storageKey: STORAGE_KEY,
  isAllowed: areFlagOverridesAllowed,
  isValidKey: isFlagKey,
})

/** The flags currently pinned from the Dev Tools panel. Always empty in production. */
export const getFlagOverrides = storage.getFlagOverrides
export const setFlagOverride = storage.setFlagOverride
export const clearFlagOverrides = storage.clearFlagOverrides
